import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  Link,
  Tabs,
  Tab,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import EmailTemplatesPanel from "../EmailTemplatesPanel";
import { createEmailConnection, getEmailConnection, testMailbox, testSavedMailbox, updateEmailConnection } from "../../services/emailChannel.api";

/**
 * Conexión de CORREO (doc 0445 E4): alta y edición desde Conexiones, sin tocar el servidor.
 * El usuario elige el proveedor y escribe solo lo imprescindible; servidores y puertos los pone el
 * sistema. La contraseña se prueba contra Gmail ANTES de guardarse y queda cifrada.
 */
const useStyles = makeStyles((theme) => ({
  title: { padding: theme.spacing(1.5, 2.5), "& h2": { fontSize: "1.05rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 10 } },
  section: { margin: theme.spacing(2, 0, 0.75), fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: theme.palette.text.secondary },
  proveedores: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 },
  tarjeta: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(1.5, 1),
    textAlign: "center",
    cursor: "pointer",
    "&:hover": { borderColor: theme.palette.primary.main },
  },
  tarjetaOff: { opacity: 0.5, cursor: "not-allowed", "&:hover": { borderColor: theme.palette.divider } },
  hint: { display: "block", marginTop: 4, color: theme.palette.text.secondary, fontSize: 12, lineHeight: 1.4 },
  prueba: { marginTop: theme.spacing(1.5), padding: theme.spacing(1, 1.5), borderRadius: 8, background: theme.palette.action.hover, fontSize: 13 },
}));

const PROVEEDORES = [
  { key: "gmail", titulo: "Gmail", detalle: "Gmail o Google Workspace", disponible: true },
  { key: "outlook", titulo: "Outlook", detalle: "Microsoft 365 · próximamente", disponible: false },
  { key: "other", titulo: "Otro", detalle: "Zoho, hosting… · próximamente", disponible: false },
];

const MOTIVO = {
  AUTH_FAILED: "Gmail rechazó la contraseña de aplicación",
  UNREACHABLE: "No se pudo contactar con el servidor de correo",
  ERROR: "El servidor devolvió un error",
};

const ERRORES = {
  ERR_EMAIL_AUTH_FAILED: "Gmail rechazó el correo o la contraseña de aplicación.",
  ERR_EMAIL_UNREACHABLE: "No se pudo contactar con Gmail. Inténtalo de nuevo.",
  ERR_EMAIL_ADDRESS_IN_USE: "Ese buzón ya está conectado en otra conexión.",
  ERR_EMAIL_INVALID_ADDRESS: "Escribe un correo válido.",
  ERR_EMAIL_SECRET_REQUIRED: "Falta la contraseña de aplicación.",
  ERR_EMAIL_INVALID_PROVIDER: "Por ahora solo está disponible Gmail.",
  ERR_WAPP_DUP_NAME: "Ya existe una conexión con ese nombre.",
};

const mostrarError = (err) => {
  const code = err?.response?.data?.error;
  if (ERRORES[code]) return toast.error(ERRORES[code], { autoClose: 8000 });
  return toastError(err);
};

const Lado = ({ titulo, r }) => {
  if (!r) return null;
  return (
    <div>
      {r.ok ? "✅" : "❌"} <b>{titulo}</b>: {r.ok ? "correcto" : MOTIVO[r.code] || r.code}
    </div>
  );
};

const EmailConnectionModal = ({ open, onClose, onSave, whatsAppId }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const editando = Boolean(whatsAppId);
  // Las plantillas son de la PLATAFORMA (alertas del servidor): solo el superadmin, y solo con la conexión creada.
  const conPlantillas = editando && Boolean(user?.super);
  const [pestana, setPestana] = useState("conexion");
  const [editandoPlantilla, setEditandoPlantilla] = useState(false);
  const [proveedor, setProveedor] = useState(null);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [password, setPassword] = useState("");
  const [lectura, setLectura] = useState(true);
  const [actual, setActual] = useState(null);
  const [prueba, setPrueba] = useState(null);
  const [probando, setProbando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPestana("conexion");
    setPrueba(null);
    setPassword("");
    if (!editando) {
      setProveedor(null);
      setName("");
      setAddress("");
      setLectura(true);
      setActual(null);
      return;
    }
    getEmailConnection(whatsAppId)
      .then(({ data }) => {
        setActual(data);
        setProveedor(data.provider);
        setName(data.connectionName || "");
        setAddress(data.address || "");
        setLectura(Boolean(data.readEnabled));
      })
      .catch(toastError);
  }, [open, whatsAppId, editando]);

  const probar = async () => {
    setProbando(true);
    setPrueba(null);
    try {
      const { data } =
        editando && !password ? await testSavedMailbox(whatsAppId) : await testMailbox({ provider: proveedor, address, appPassword: password, readEnabled: lectura });
      setPrueba(data);
    } catch (err) {
      mostrarError(err);
    } finally {
      setProbando(false);
    }
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      if (editando) {
        await updateEmailConnection(whatsAppId, { name, provider: proveedor, address, appPassword: password || undefined, readEnabled: lectura });
        toast.success("Conexión de correo actualizada");
      } else {
        await createEmailConnection({ name, provider: proveedor, address, appPassword: password, readEnabled: lectura });
        toast.success(lectura ? "Correo conectado: los correos que lleguen abrirán tickets" : "Correo conectado (solo envío)");
      }
      onSave && onSave();
      onClose();
    } catch (err) {
      mostrarError(err);
    } finally {
      setGuardando(false);
    }
  };

  const estado = () => {
    if (!actual) return null;
    if (actual.lastErrorCode) return <Chip size="small" label={`Error: ${MOTIVO[actual.lastErrorCode] || actual.lastErrorCode}`} style={{ background: "#fdecea", color: "#b71c1c" }} />;
    if (!actual.readEnabled) return <Chip size="small" label="Solo envío" />;
    return actual.reading ? <Chip size="small" label="Leyendo el buzón" style={{ background: "#e8f5e9", color: "#2e7d32" }} /> : <Chip size="small" label="Desconectada" />;
  };

  const paso1 = (
    <>
      <Typography variant="body2" color="textSecondary">
        Conecta el buzón de tu empresa: los correos que lleguen se convierten en tickets y respondes desde el panel, en el mismo hilo.
      </Typography>
      <Typography className={classes.section}>¿Qué correo vas a conectar?</Typography>
      <div className={classes.proveedores}>
        {PROVEEDORES.map((p) => (
          <div
            key={p.key}
            className={`${classes.tarjeta} ${p.disponible ? "" : classes.tarjetaOff}`}
            onClick={() => p.disponible && setProveedor(p.key)}
          >
            <img src="/email.png" alt="" style={{ width: 28, height: 28 }} />
            <Typography variant="body2" style={{ fontWeight: 600, marginTop: 4 }}>{p.titulo}</Typography>
            <Typography variant="caption" color="textSecondary">{p.detalle}</Typography>
          </div>
        ))}
      </div>
    </>
  );

  const paso2 = (
    <>
      {editando && <Box mb={1.5} display="flex" alignItems="center" style={{ gap: 8 }}>{estado()}{actual?.lastErrorDescription && <Typography variant="caption" color="textSecondary">{actual.lastErrorDescription}</Typography>}</Box>}
      <TextField label="Nombre de la conexión" placeholder="Ej.: Soporte" value={name} onChange={(e) => setName(e.target.value)} variant="outlined" size="small" fullWidth margin="dense" />
      <TextField label="Correo de Gmail" placeholder="soporte@tuempresa.com" value={address} onChange={(e) => setAddress(e.target.value)} variant="outlined" size="small" fullWidth margin="dense" />
      <TextField
        label="Contraseña de aplicación"
        type="password"
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={editando ? "Guardada — escribe otra solo para cambiarla" : "16 letras"}
        InputLabelProps={editando ? { shrink: true } : undefined}
        variant="outlined"
        size="small"
        fullWidth
        margin="dense"
      />
      <span className={classes.hint}>
        No es tu contraseña de Gmail: es una clave para aplicaciones. Créala en{" "}
        <Link href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer">myaccount.google.com/apppasswords</Link>{" "}
        (necesita la verificación en dos pasos activada).
      </span>
      <FormControlLabel
        style={{ marginTop: 8 }}
        control={<Switch size="small" color="primary" checked={lectura} onChange={(e) => setLectura(e.target.checked)} />}
        label={<Typography variant="body2">Leer los correos que llegan y abrir tickets</Typography>}
      />
      <span className={classes.hint}>Apagado, la conexión solo envía. Al conectar no se importan los correos antiguos: se empieza desde ahora.</span>
      {prueba && (
        <div className={classes.prueba}>
          <Lado titulo="Lectura (IMAP)" r={prueba.imap} />
          <Lado titulo="Envío (SMTP)" r={prueba.smtp} />
        </div>
      )}
    </>
  );

  const listo = proveedor && address.trim() && (editando || password.trim());

  return (
    <Dialog open={open} onClose={(_, reason) => !guardando && reason !== "backdropClick" && onClose()} maxWidth={pestana === "plantillas" && editandoPlantilla ? "md" : "sm"} fullWidth>
      <DialogTitle className={classes.title}>
        <img src="/email.png" alt="" style={{ width: 26, height: 26 }} />
        {editando ? "Editar conexión — Correo" : "Conectar correo"}
      </DialogTitle>
      {conPlantillas && (
        <Tabs value={pestana} onChange={(_, v) => setPestana(v)} indicatorColor="primary" textColor="primary" style={{ padding: "0 16px" }}>
          <Tab value="conexion" label="Conexión" />
          <Tab value="plantillas" label="Plantillas" />
        </Tabs>
      )}
      <DialogContent dividers>
        {pestana === "plantillas" && conPlantillas ? (
          <EmailTemplatesPanel whatsappId={whatsAppId} onEditingChange={setEditandoPlantilla} />
        ) : proveedor ? (
          paso2
        ) : (
          paso1
        )}
      </DialogContent>
      <DialogActions>
        {!editando && proveedor && <Button onClick={() => setProveedor(null)} disabled={guardando} style={{ marginRight: "auto" }}>Atrás</Button>}
        <Button onClick={onClose} disabled={guardando}>{pestana === "plantillas" ? "Cerrar" : "Cancelar"}</Button>
        {proveedor && pestana === "conexion" && (
          <Button onClick={probar} disabled={!listo || probando || guardando}>
            {probando ? <CircularProgress size={18} /> : "Probar"}
          </Button>
        )}
        {proveedor && pestana === "conexion" && (
          <Button color="primary" variant="contained" onClick={guardar} disabled={!listo || guardando}>
            {guardando ? <CircularProgress size={18} /> : editando ? "Guardar" : "Conectar"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EmailConnectionModal;
